import { useEffect, useId, useState } from 'react';
import type { ReminderCadence } from '@intentionalist/shared';
import { disableReminders, enableReminders, reminderState, type ReminderState } from './push.js';

/**
 * Review reminders (FR-5.12).
 *
 * The cadence is a choice and `off` is one of its values, because the
 * requirement asks for configurable *or disabled* and because a product whose
 * whole argument is deliberateness should not make turning it off the awkward
 * path.
 *
 * Every state the browser can be in is named rather than collapsed into "not
 * available". A browser that cannot do this at all, a build with no key, a
 * permission the user revoked and a browser that simply has not been asked are
 * four different situations with four different remedies, and only one of them
 * is something the user can act on here (NFR-UX-8).
 */
const CADENCES: { value: Exclude<ReminderCadence, 'off'>; label: string }[] = [
  { value: 'daily', label: 'every day' },
  { value: 'weekly', label: 'once a week, on Monday' },
];

export function Reminders() {
  const ids = { cadence: useId() };
  const [state, setState] = useState<ReminderState | null>(null);
  const [cadence, setCadence] = useState<Exclude<ReminderCadence, 'off'>>('weekly');
  const [busy, setBusy] = useState(false);
  const [fault, setFault] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    void reminderState().then((current) => {
      if (!live) return;
      setState(current);
      if (current.kind === 'on' && current.cadence !== 'off') setCadence(current.cadence);
    });
    return () => {
      live = false;
    };
  }, []);

  const run = async (work: () => Promise<ReminderState>) => {
    setBusy(true);
    setFault(null);
    try {
      setState(await work());
    } catch {
      // The permission, the subscription and the server call fail for different
      // reasons; what the user can do about any of them is the same.
      setFault('Reminders could not be changed. Nothing was altered.');
    } finally {
      setBusy(false);
    }
  };

  if (state === null) {
    return (
      <p className="muted" role="status">
        checking whether this browser can send reminders…
      </p>
    );
  }

  if (state.kind === 'unsupported') {
    return <p className="muted">This browser cannot send reminders.</p>;
  }

  if (state.kind === 'unconfigured') {
    // A build-time omission rather than anything the user did.
    return <p className="muted">Reminders are not configured for this build.</p>;
  }

  if (state.kind === 'blocked') {
    return (
      <p className="muted">
        Notifications are blocked for this site. Allowing them again is in the browser&apos;s own
        site settings — a page cannot ask twice.
      </p>
    );
  }

  return (
    <div className="field">
      <label htmlFor={ids.cadence}>Remind me about reviews</label>
      <select
        id={ids.cadence}
        value={state.kind === 'on' ? cadence : 'off'}
        disabled={busy}
        onChange={(e) => {
          const chosen = e.target.value;
          if (chosen === 'off') {
            void run(disableReminders);
            return;
          }
          const next = chosen as Exclude<ReminderCadence, 'off'>;
          setCadence(next);
          // Choosing a cadence is the request: a separate "enable" button would
          // mean two steps for one decision, and the permission prompt is the
          // only confirmation that matters.
          void run(() => enableReminders(next));
        }}
      >
        <option value="off">never</option>
        {CADENCES.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>

      <p className="muted hint">
        {state.kind === 'on'
          ? 'Sent only when something is actually due. A reminder says how many items are waiting and nothing about what to decide.'
          : 'Your browser will ask permission first. Reminders are sent only when something is due.'}
      </p>

      {fault !== null && (
        <p className="warn" role="alert">
          {fault}
        </p>
      )}
    </div>
  );
}
